import { Directive, OnDestroy } from '@angular/core';

// Selector-less directive so subclasses keep a real ɵdir. An @Injectable base
// with ngOnDestroy is recompiled by the test JIT, which replaces ɵfac with a
// getter and then breaks the next component bundle.
@Directive({ standalone: false })
export class ModalScrollBlockBase implements OnDestroy {
  keyboardOffset = 0;
  availableHeight: number | null = null;
  private _viewportResizeHandler?: () => void;
  private _savedScrollY = 0;

  constructor() {
    this._savedScrollY = window.scrollY;
    const bodyElement = document.body;
    bodyElement.classList.add('block-vertical-scroll');
    this.setupKeyboardDetection();
  }

  ngOnDestroy(): void {
    const bodyElement = document.body;
    bodyElement.classList.remove('block-vertical-scroll');
    this.cleanupKeyboardDetection();
    requestAnimationFrame(() => window.scrollTo(0, this._savedScrollY));
  }

  /**
   * Set up keyboard detection for webapp on mobile devices
   * Uses Visual Viewport API to detect when keyboard is shown
   */
  private setupKeyboardDetection(): void {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    // Check if device is mobile by checking screen width
    const isMobile = window.innerWidth < 768; // md breakpoint in tailwind

    if (isMobile) {
      this.initializeViewportListener();
    }
  }

  /**
   * Initialize the visual viewport resize listener
   */
  private initializeViewportListener(): void {
    if (!window.visualViewport) return;

    this._viewportResizeHandler = () => {
      this.adjustModalPosition();
    };

    window.visualViewport.addEventListener('resize', this._viewportResizeHandler);
    window.visualViewport.addEventListener('scroll', this._viewportResizeHandler);

    // Initial adjustment
    this.adjustModalPosition();
  }

  /**
   * Calculate and apply modal position adjustment based on keyboard visibility
   */
  private adjustModalPosition(): void {
    if (!window.visualViewport) return;

    const visualViewport = window.visualViewport;
    const windowHeight = window.innerHeight;
    const viewportHeight = visualViewport.height;
    const offsetTop = visualViewport.offsetTop || 0;

    // Calculate keyboard height (difference between window and viewport + offset)
    const keyboardHeight = windowHeight - viewportHeight - offsetTop;

    // Only adjust if keyboard is actually visible (threshold of 150px to avoid false positives)
    if (keyboardHeight > 150) {
      this.keyboardOffset = keyboardHeight;
      // Calculate available height for modal content (viewport height minus some padding)
      this.availableHeight = viewportHeight;
    } else {
      this.keyboardOffset = 0;
      this.availableHeight = null;
    }
  }

  /**
   * Clean up viewport listeners
   */
  private cleanupKeyboardDetection(): void {
    if (window.visualViewport && this._viewportResizeHandler) {
      window.visualViewport.removeEventListener('resize', this._viewportResizeHandler);
      window.visualViewport.removeEventListener('scroll', this._viewportResizeHandler);
      this._viewportResizeHandler = undefined;
    }
    this.keyboardOffset = 0;
  }
}
